import { useRef, useCallback } from 'react';

/**
 * useAlarmSound
 * 4 tipos de alarma usando Web Audio API.
 *
 * type: 'emergency' | 'medical' | 'industrial' | 'system'
 *
 * Uso:
 *   const { playAlarm, stopAlarm } = useAlarmSound({ type: 'emergency' });
 */
const useAlarmSound = (options = {}) => {
  const {
    type   = 'emergency',
    volume = 0.5,
  } = options;

  const audioCtxRef  = useRef(null);
  const intervalRef  = useRef(null);
  const isPlayingRef = useRef(false);

  /* ── AudioContext ───────────────────────────────────────── */
  const getCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  /* ── Helper: reproduce una nota con frecuencia y duración ── */
  const playTone = useCallback((ctx, freq, startTime, dur, wave = 'sawtooth') => {
    const gain = ctx.createGain();
    const osc  = ctx.createOscillator();

    osc.type = wave;
    osc.frequency.setValueAtTime(freq, startTime);

    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(volume, startTime + 0.01);
    gain.gain.linearRampToValueAtTime(0, startTime + dur);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + dur + 0.01);
  }, [volume]);

  /* ── Helper: frecuencia que sube o baja suavemente (sweep) ─ */
  const playSweep = useCallback((ctx, freqStart, freqEnd, startTime, dur, wave = 'sawtooth') => {
    const gain = ctx.createGain();
    const osc  = ctx.createOscillator();

    osc.type = wave;
    osc.frequency.setValueAtTime(freqStart, startTime);
    osc.frequency.linearRampToValueAtTime(freqEnd, startTime + dur);

    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(volume, startTime + 0.02);
    gain.gain.linearRampToValueAtTime(volume * 0.8, startTime + dur - 0.05);
    gain.gain.linearRampToValueAtTime(0, startTime + dur);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + dur + 0.01);
  }, [volume]);

  /* ══════════════════════════════════════════════════════════
     TIPO 1 — emergency: wee-woo sube y baja (sirena clásica)
  ══════════════════════════════════════════════════════════ */
  const playEmergency = useCallback(() => {
    const ctx  = getCtx();
    const now  = ctx.currentTime;
    const step = 0.5; // duración de cada sweep
    playSweep(ctx, 600,  1100, now,        step, 'sawtooth');
    playSweep(ctx, 1100, 600,  now + step, step, 'sawtooth');
    return step * 2; // devuelve duración total del ciclo
  }, [getCtx, playSweep]);

  /* ══════════════════════════════════════════════════════════
     TIPO 2 — medical: beep triple rápido (monitor cardíaco)
  ══════════════════════════════════════════════════════════ */
  const playMedical = useCallback(() => {
    const ctx = getCtx();
    const now = ctx.currentTime;
    [0, 0.18, 0.36].forEach(offset => {
      playTone(ctx, 1040, now + offset, 0.12, 'sine');
    });
    return 0.8; // ciclo completo con pausa
  }, [getCtx, playTone]);

  /* ══════════════════════════════════════════════════════════
     TIPO 3 — industrial: pulso grave y cortante
  ══════════════════════════════════════════════════════════ */
  const playIndustrial = useCallback(() => {
    const ctx = getCtx();
    const now = ctx.currentTime;
    // Tono bajo pulsante con harmónico
    playTone(ctx, 220, now,        0.35, 'square');
    playTone(ctx, 440, now,        0.35, 'square');
    playTone(ctx, 220, now + 0.45, 0.35, 'square');
    playTone(ctx, 440, now + 0.45, 0.35, 'square');
    return 1.0;
  }, [getCtx, playTone]);

  /* ══════════════════════════════════════════════════════════
     TIPO 4 — system: dos tonos alternantes (alerta de red/IT)
  ══════════════════════════════════════════════════════════ */
  const playSystem = useCallback(() => {
    const ctx = getCtx();
    const now = ctx.currentTime;
    playTone(ctx, 880, now,        0.18, 'square');
    playTone(ctx, 660, now + 0.22, 0.18, 'square');
    return 0.55;
  }, [getCtx, playTone]);

  /* ── Mapa de tipos ──────────────────────────────────────── */
  const TYPES = {
    emergency: playEmergency,
    medical:   playMedical,
    industrial: playIndustrial,
    system:    playSystem,
  };

  /* ── playAlarm: repite el ciclo seleccionado ────────────── */
  const playAlarm = useCallback(() => {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    const playFn    = TYPES[type] || playEmergency;
    const cycleDur  = playFn();                          // primer ciclo inmediato
    intervalRef.current = setInterval(playFn, cycleDur * 1000);
  }, [type, TYPES, playEmergency]);

  /* ── stopAlarm ──────────────────────────────────────────── */
  const stopAlarm = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current  = null;
    isPlayingRef.current = false;
  }, []);

  /* ── pingOnce: un solo ciclo sin repetir ────────────────── */
  const pingOnce = useCallback(() => {
    const playFn = TYPES[type] || playEmergency;
    playFn();
  }, [type, TYPES, playEmergency]);

  return { playAlarm, stopAlarm, pingOnce, isPlaying: isPlayingRef };
};

export default useAlarmSound;